// src/modules/hr/pages/HrReports.jsx — Phase 24 (Reports)
import React, { useState } from 'react';
import { Card, Table, Typography, Row, Col, Button, Select, message, Space, Tag } from 'antd';
import { FileTextOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchEmployees, fetchAssets, fetchAssetAssignments, fetchLeaves, fetchExits,
} from '../api';

const { Title, Text } = Typography;

const REPORTS = {
  EMPLOYEE_MASTER: {
    label: 'Employee Master',
    fetch: () => fetchEmployees({ limit: 5000 }),
    pick: (r) => r?.data?.data || [],
    columns: [
      ['employeeCode', 'Code'], ['name', 'Name'], ['email', 'Email'], ['phone', 'Phone'],
      ['designation', 'Designation'], ['department', 'Department'], ['branch.name', 'Branch'],
      ['employmentType', 'Type'], ['employmentStatus', 'Status'],
    ],
  },
  ASSET_REGISTER: {
    label: 'Asset Register',
    fetch: () => fetchAssets({ limit: 5000 }),
    pick: (r) => r?.data?.data || [],
    columns: [
      ['assetCode', 'Code'], ['name', 'Name'], ['category', 'Category'], ['serialNumber', 'Serial'],
      ['branch.name', 'Branch'], ['status', 'Status'], ['assignedToEmployee.name', 'Holder'],
    ],
  },
  ASSET_ASSIGNMENTS: {
    label: 'Asset Assignment / Return',
    fetch: () => fetchAssetAssignments({ limit: 5000 }),
    pick: (r) => r?.data?.data || [],
    columns: [
      ['asset.assetCode', 'Asset'], ['asset.name', 'Name'], ['assigneeType', 'Assignee'],
      ['employee.name', 'Employee'], ['branch.name', 'Branch'], ['status', 'Status'],
      ['issueDate', 'Issued'], ['returnedDate', 'Returned'], ['penalty', 'Penalty'],
    ],
  },
  LEAVE: {
    label: 'Leave Report',
    fetch: () => fetchLeaves({ limit: 5000 }),
    pick: (r) => r?.data?.data || [],
    columns: [
      ['reference', 'Ref'], ['employee.name', 'Employee'], ['leaveType', 'Type'],
      ['fromDate', 'From'], ['toDate', 'To'], ['days', 'Days'], ['status', 'Status'],
    ],
  },
  EXIT: {
    label: 'Exit Report',
    fetch: () => fetchExits({ limit: 5000 }),
    pick: (r) => r?.data?.data || [],
    columns: [
      ['reference', 'Ref'], ['employee.name', 'Employee'], ['exitType', 'Type'],
      ['resignationDate', 'Resignation'], ['lastWorkingDate', 'Last Working'], ['status', 'Status'],
    ],
  },
};

const get = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);
const fmt = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return dayjs(v).format('DD/MM/YYYY');
  return v;
};

export default function HrReports() {
  const [reportKey, setReportKey] = useState('EMPLOYEE_MASTER');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const report = REPORTS[reportKey];

  const load = async () => {
    setLoading(true);
    try {
      const res = await report.fetch();
      setRows(report.pick(res));
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportCsv = () => {
    if (!rows.length) return message.warning('Load the report first');
    const header = report.columns.map(([, l]) => `"${l}"`).join(',');
    const body = rows.map((row) => report.columns.map(([p]) => `"${String(fmt(get(row, p)) ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const csv = `${header}\n${body}`;
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${reportKey.toLowerCase()}-${dayjs().format('YYYYMMDD')}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const columns = report.columns.map(([p, l]) => ({
    title: l, key: p,
    render: (_, row) => {
      const v = fmt(get(row, p));
      return /status|Status/.test(p) ? <Tag>{v}</Tag> : (v ?? '—');
    },
  }));

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><FileTextOutlined style={{ marginRight: 8 }} />HR Reports</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Employee, asset, leave & exit registers — export to CSV</Text>
        </Col>
        <Col>
          <Space>
            <Select value={reportKey} style={{ width: 240 }} onChange={(v) => { setReportKey(v); setRows([]); }}
              options={Object.entries(REPORTS).map(([k, r]) => ({ label: r.label, value: k }))} />
            <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>Load</Button>
            <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>
          </Space>
        </Col>
      </Row>

      <Card size="small">
        <Table rowKey={(r) => r._id || JSON.stringify(r)} columns={columns} dataSource={rows} loading={loading}
          size="small" scroll={{ x: 1100 }} pagination={{ pageSize: 50, showTotal: (t) => `${t} rows` }}
          locale={{ emptyText: 'Load a report to see results' }} />
      </Card>
    </div>
  );
}
